import { useState, useEffect, useRef } from "react";

export default function Loyalty() {
  const [flipped, setFlipped] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    const els = sectionRef.current?.querySelectorAll(".reveal-on-scroll");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="promotion" ref={sectionRef} className="py-20 md:py-28 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-14 reveal-on-scroll">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase text-accent-600 bg-accent-100 rounded-full">
            Rewards
          </span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-4">
            BB COFFEE <span className="text-primary-600">Loyalty Card</span>
          </h2>
          <p className="text-foreground-500 text-sm md:text-base">Collect stamps with every drink and your next one is on us!</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center max-w-5xl mx-auto">
          <div className="reveal-on-scroll mx-auto w-full max-w-[380px]" role="button" tabIndex={0} aria-label="Tap to flip the loyalty card and see the back" onClick={() => setFlipped(!flipped)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setFlipped(!flipped); }}>
            <div className={`flip-card cursor-pointer ${flipped ? "flipped" : ""}`}>
              <div className="flip-card-inner aspect-[4/3]">
                {/* Front */}
                <div className="flip-card-front bg-[#F5F0E0] rounded-xl overflow-hidden border border-[#E5DDD0]/50 p-4 sm:p-5 h-full flex flex-col justify-between">
                  <div className="flex gap-3 sm:gap-4 flex-1 min-h-0">
                    <div className="grid grid-cols-3 gap-2 sm:gap-3 flex-1">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                        <div key={n} className="w-full aspect-square">
                          <div className="stamp-cup">
                            <div className="cup-inner">
                              <div className="froth-dots">
                                <span className="w-1.5 h-1.5" />
                                <span className="w-2 h-2" />
                                <span className="w-1.5 h-1.5" />
                                <span className="w-2 h-2" />
                                <span className="w-1.5 h-1.5" />
                              </div>
                              <div className="cup-handle" />
                              <div className="relative z-10 flex flex-col items-center justify-center leading-none text-center">
                                {n === 2 ? (
                                  <>
                                    <span className="font-bebas tracking-wide drop-shadow-sm text-[8px] sm:text-[9px] text-amber-200">5%</span>
                                    <span className="text-[6px] sm:text-[7px] font-bebas tracking-wide text-amber-200/80 drop-shadow-sm">OFF</span>
                                  </>
                                ) : n === 6 ? (
                                  <>
                                    <span className="font-bebas tracking-wide drop-shadow-sm text-[8px] sm:text-[9px] text-amber-200">10%</span>
                                    <span className="text-[6px] sm:text-[7px] font-bebas tracking-wide text-amber-200/80 drop-shadow-sm">OFF</span>
                                  </>
                                ) : n === 7 ? (
                                  <>
                                    <span className="text-[5px] sm:text-[6px] font-bebas tracking-wide text-amber-200 drop-shadow-sm">2ND</span>
                                    <span className="text-[5px] sm:text-[6px] font-bebas tracking-wide text-amber-200 drop-shadow-sm">DRINK 20%</span>
                                  </>
                                ) : (
                                  <span className="font-bebas tracking-wide drop-shadow-sm text-[9px] sm:text-[10px] text-white/90">{n}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-col items-center justify-start gap-3 w-[72px] sm:w-[84px] flex-shrink-0 pt-1">
                      <h3 className="font-bebas text-[14px] sm:text-[16px] text-foreground-950 text-center leading-none tracking-[0.12em]">
                        BB<br />COFFEE
                      </h3>
                      <div className="relative w-[62px] h-[62px] sm:w-[72px] sm:h-[72px] mt-1">
                        <div className="absolute inset-0 rounded-full bg-[#D4A017]" />
                        <div className="absolute inset-[3px] rounded-full bg-[#C6900A] flex flex-col items-center justify-center">
                          <span className="text-[9px] sm:text-[10px] font-bold text-white leading-tight text-center font-bebas tracking-wider">FREE!</span>
                          <span className="text-[7px] sm:text-[8px] font-bold text-white/90 leading-tight text-center font-bebas tracking-wide">1 DRINK</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Back */}
                <div className="flip-card-back bg-[#4A2E1D] rounded-xl overflow-hidden absolute inset-0">
                  <div className="absolute inset-0 flex items-center justify-start pl-4 sm:pl-6 pointer-events-none">
                    <span className="font-bebas text-[70px] sm:text-[90px] text-[#F5E6D3]/20 leading-none tracking-wider select-none">BB</span>
                  </div>
                  <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-1.5 p-3 sm:p-4 ml-[85px] sm:ml-[105px]">
                    {[
                      "https://readdy.ai/api/search-image?query=Roasted%20coffee%20beans%20pile%20close%20up%20top%20view%20food%20photography%20warm%20tones%20dark%20background&width=120&height=120&seq=ing-1&orientation=squarish",
                      "https://readdy.ai/api/search-image?query=Fresh%20red%20raspberries%20close%20up%20top%20view%20food%20photography%20warm%20tones%20dark%20background&width=120&height=120&seq=ing-2&orientation=squarish",
                      "https://readdy.ai/api/search-image?query=Fresh%20ripe%20peaches%20close%20up%20top%20view%20food%20photography%20warm%20tones%20dark%20background&width=120&height=120&seq=ing-3&orientation=squarish",
                      "https://readdy.ai/api/search-image?query=Black%20tapioca%20boba%20pearls%20close%20up%20top%20view%20food%20photography%20warm%20tones%20dark%20background&width=120&height=120&seq=ing-4&orientation=squarish",
                      "https://readdy.ai/api/search-image?query=Green%20tea%20leaves%20close%20up%20top%20view%20food%20photography%20warm%20tones%20dark%20background&width=120&height=120&seq=ing-5&orientation=squarish",
                      "https://readdy.ai/api/search-image?query=Ground%20coffee%20powder%20close%20up%20top%20view%20food%20photography%20warm%20tones%20dark%20background&width=120&height=120&seq=ing-6&orientation=squarish",
                      "https://readdy.ai/api/search-image?query=Fresh%20lychee%20fruit%20close%20up%20top%20view%20food%20photography%20warm%20tones%20dark%20background&width=120&height=120&seq=ing-7&orientation=squarish",
                      "https://readdy.ai/api/search-image?query=Fresh%20lime%20slices%20close%20up%20top%20view%20food%20photography%20warm%20tones%20dark%20background&width=120&height=120&seq=ing-8&orientation=squarish",
                      "https://readdy.ai/api/search-image?query=Fresh%20blackberries%20close%20up%20top%20view%20food%20photography%20warm%20tones%20dark%20background&width=120&height=120&seq=ing-9&orientation=squarish",
                    ].map((src, i) => (
                      <img
                        key={i}
                        alt="ingredient"
                        className="w-full h-full object-cover rounded-md"
                        src={src}
                      />
                    ))}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-start pl-4 sm:pl-6 pointer-events-none">
                    <span className="font-bebas text-[48px] sm:text-[64px] text-[#F5E6D3] leading-none tracking-wider select-none drop-shadow-sm">BB</span>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-foreground-400 mt-3 flex items-center justify-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-right-line" />
              </div>
              Tap or click the card to flip
            </p>
          </div>

          <div className="reveal-on-scroll space-y-6">
            <div className="space-y-4">
              {[
                { num: "1", title: "Grab Your Card", text: "Ask our barista for a loyalty card on your next visit. It's completely free!", accent: false },
                { num: "2", title: "Collect Stamps", text: "Get 1 stamp for every drink you purchase. Any drink on our menu counts!", accent: false },
                { num: "3", title: "Get Your Free Drink", text: "Collect 9 stamps and your 10th drink is on us. Any drink, any size, absolutely free!", accent: true },
              ].map((step) => (
                <div key={step.num} className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${step.accent ? "bg-accent-100" : "bg-primary-100"}`}>
                    <span className={`text-sm font-bold ${step.accent ? "text-accent-600" : "text-primary-600"}`}>{step.num}</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{step.title}</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-primary-50 rounded-xl p-5 border border-primary-200/50">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-information-line text-lg text-primary-600" />
                </div>
                <h4 className="font-heading font-semibold text-foreground-900 text-sm">Good to Know</h4>
              </div>
              <ul className="space-y-1.5 text-xs text-foreground-500">
                {[
                  "One stamp per drink per visit",
                  "Valid at BB COFFEE truck location only",
                  "Free drink can be any item on the menu",
                  "Bonus rewards at stamps 2, 6 and 7 with discounts along the way!",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-primary-500 mt-0.5" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}